import type { AmbienceRecipe } from './catalog';

/**
 * Procedural venue ambience (SPEC_V9 §B1): WebAudio only — no audio files.
 * Every sound is noise or oscillators shaped by filters, envelopes and slow
 * LFOs, with small random events (a drop on the tin roof, a page turning, a
 * cricket, a crackle) scheduled on timers:
 *
 * | recipe        | venue          | what you hear                              |
 * |---------------|----------------|--------------------------------------------|
 * | rain_kettle   | chai tapri     | rain, drops on a tin roof, a kettle's hiss |
 * | wind_city     | rooftop        | breeze gusts, a distant city rumble, cars  |
 * | waves         | beach          | waves rolling in and washing out, foam     |
 * | pages_rain    | bookstore café | room tone, rain on the glass, pages        |
 * | crickets_fire | stargazing     | two crickets, a campfire roar, crackles    |
 * | fire_pad      | candlelight    | a soft fire and a warm, slow chord pad     |
 *
 * It plays on this device only, quietly by default. While muted (or volume
 * 0) there is no AudioContext, no node and no timer at all — zero cost. It
 * dips (ducks) while your own mic is picking you up.
 */

/** The slice of BaseAudioContext the engine uses (a fake in tests). */
export type AudioCtx = Pick<
  AudioContext,
  'createGain' | 'createBiquadFilter' | 'createBufferSource' | 'createOscillator' | 'createBuffer' | 'destination' | 'currentTime' | 'sampleRate' | 'state' | 'resume' | 'close'
>;

export interface AmbienceDeps {
  /** Makes an AudioContext (null when WebAudio isn't available). */
  createContext?: () => AudioCtx | null;
  /** Random source (tests pass a seeded one). */
  random?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
}

/** Master level at volume 1 (the recipes are mixed to peak around here). */
export const MASTER_MAX = 0.6;
/** Default volume (0..1): quiet — ambience sits under the conversation (v14: 30 %). */
export const DEFAULT_VOLUME = 0.3;
/** While your own mic is active the ambience dips to this share. */
export const DUCK_LEVEL = 0.3;
/** v16: a game moment (a result) swells the place this much for a beat. */
export const SWELL_LEVEL = 1.8;
export const SWELL_MS = 2600;
/** Fade-in / fade-out times (seconds). */
export const FADE_IN_S = 1.8;
export const FADE_OUT_S = 0.5;

function defaultContext(): AudioCtx | null {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  try {
    return new Ctx({ latencyHint: 'playback' });
  } catch {
    return null;
  }
}

type NoiseKind = 'white' | 'pink' | 'brown';

/** A looping noise buffer (Paul Kellet's pink, leaky-integrator brown). */
function noiseBuffer(ctx: AudioCtx, kind: NoiseKind, rnd: () => number, seconds = 3): AudioBuffer {
  const n = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0,
    b1 = 0,
    b2 = 0,
    b3 = 0,
    b4 = 0,
    b5 = 0,
    b6 = 0,
    last = 0;
  for (let i = 0; i < n; i++) {
    const w = rnd() * 2 - 1;
    if (kind === 'white') d[i] = w * 0.5;
    else if (kind === 'pink') {
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    } else {
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    }
  }
  return buf;
}

interface FilterSpec {
  type: BiquadFilterType;
  freq: number;
  q?: number;
}

/**
 * The building blocks a recipe uses. Every node it makes is connected into
 * `out`; every source and timer is tracked so stop() tears it all down.
 */
export class Kit {
  readonly sources: AudioScheduledSourceNode[] = [];
  private timers = new Set<unknown>();
  private noise = new Map<NoiseKind, AudioBuffer>();
  private stopped = false;

  constructor(
    readonly ctx: AudioCtx,
    readonly out: AudioNode,
    readonly rnd: () => number,
    private st: (fn: () => void, ms: number) => unknown,
    private ct: (id: unknown) => void,
  ) {}

  buffer(kind: NoiseKind): AudioBuffer {
    let b = this.noise.get(kind);
    if (!b) {
      b = noiseBuffer(this.ctx, kind, this.rnd);
      this.noise.set(kind, b);
    }
    return b;
  }

  gain(value: number, to: AudioNode = this.out): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    g.connect(to);
    return g;
  }

  filters(chain: FilterSpec[], to: AudioNode): AudioNode {
    let next = to;
    for (let i = chain.length - 1; i >= 0; i--) {
      const f = this.ctx.createBiquadFilter();
      f.type = chain[i]!.type;
      f.frequency.value = chain[i]!.freq;
      if (chain[i]!.q != null) f.Q.value = chain[i]!.q!;
      f.connect(next);
      next = f;
    }
    return next;
  }

  /** A looping noise bed through a filter chain; returns its gain. */
  bed(kind: NoiseKind, level: number, chain: FilterSpec[]): GainNode {
    const g = this.gain(level);
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer(kind);
    src.loop = true;
    src.connect(this.filters(chain, g));
    src.start(this.ctx.currentTime, this.rnd() * 2);
    this.sources.push(src);
    return g;
  }

  /** A slow LFO wobbling an AudioParam around its current value. */
  lfo(param: AudioParam, rate: number, depth: number, shape: OscillatorType = 'sine') {
    const osc = this.ctx.createOscillator();
    osc.type = shape;
    osc.frequency.value = rate;
    const amt = this.ctx.createGain();
    amt.gain.value = depth;
    osc.connect(amt);
    amt.connect(param);
    osc.start(this.ctx.currentTime + this.rnd() * 0.5);
    this.sources.push(osc);
  }

  /** A short noise burst with an attack/decay envelope (a drop, a crackle, a page). */
  burst(kind: NoiseKind, opts: { at?: number; level: number; attack: number; decay: number; chain: FilterSpec[]; to?: AudioNode }) {
    const t = opts.at ?? this.ctx.currentTime;
    const g = this.gain(0, opts.to ?? this.out);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(opts.level, t + opts.attack);
    g.gain.setTargetAtTime(0, t + opts.attack, opts.decay / 3);
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer(kind);
    src.connect(this.filters(opts.chain, g));
    src.start(t, this.rnd() * 2);
    src.stop(t + opts.attack + opts.decay + 0.05);
  }

  /** A held tone (pads, a cricket's carrier). */
  tone(freq: number, level: number, shape: OscillatorType = 'sine', to: AudioNode = this.out): { osc: OscillatorNode; gain: GainNode } {
    const g = this.gain(level, to);
    const osc = this.ctx.createOscillator();
    osc.type = shape;
    osc.frequency.value = freq;
    osc.connect(g);
    osc.start(this.ctx.currentTime);
    this.sources.push(osc);
    return { osc, gain: g };
  }

  /** Runs fn now-ish and then again every [min, max] ms until stop(). */
  every(minMs: number, maxMs: number, fn: () => void) {
    const loop = () => {
      if (this.stopped) return;
      const id = this.st(() => {
        this.timers.delete(id);
        if (this.stopped) return;
        fn();
        loop();
      }, minMs + this.rnd() * (maxMs - minMs));
      this.timers.add(id);
    };
    loop();
  }

  get timerCount(): number {
    return this.timers.size;
  }

  stop(at: number) {
    this.stopped = true;
    for (const id of this.timers) this.ct(id);
    this.timers.clear();
    for (const s of this.sources) {
      try {
        s.stop(at);
      } catch {
        /* already stopped */
      }
    }
  }
}

/** The six recipes. Levels are relative; the master sets the volume. */
export const RECIPES: Record<AmbienceRecipe, (k: Kit) => void> = {
  // Monsoon rain on a tin roof, the odd heavy drop, and a kettle that comes
  // to the boil now and then.
  rain_kettle(k) {
    const rain = k.bed('pink', 0.5, [
      { type: 'highpass', freq: 500 },
      { type: 'lowpass', freq: 7000 },
    ]);
    k.lfo(rain.gain, 0.07, 0.12);
    k.bed('brown', 0.25, [{ type: 'lowpass', freq: 300 }]);
    k.every(60, 220, () =>
      k.burst('white', { level: 0.18 + k.rnd() * 0.22, attack: 0.002, decay: 0.05, chain: [{ type: 'bandpass', freq: 2400 + k.rnd() * 2200, q: 6 }] }),
    );
    const kettle = k.bed('white', 0, [
      { type: 'bandpass', freq: 3600, q: 3 },
      { type: 'highpass', freq: 1800 },
    ]);
    k.every(14_000, 26_000, () => {
      const t = k.ctx.currentTime;
      kettle.gain.cancelScheduledValues(t);
      kettle.gain.setValueAtTime(kettle.gain.value, t);
      kettle.gain.linearRampToValueAtTime(0.09, t + 5);
      kettle.gain.linearRampToValueAtTime(0, t + 9);
    });
  },
  // A warm breeze in gusts, the city far below, a car sweeping past.
  wind_city(k) {
    const wind = k.bed('brown', 0.55, [
      { type: 'lowpass', freq: 700 },
      { type: 'highpass', freq: 60 },
    ]);
    k.lfo(wind.gain, 0.06, 0.25);
    k.bed('brown', 0.3, [{ type: 'lowpass', freq: 140 }]);
    k.bed('pink', 0.05, [
      { type: 'bandpass', freq: 900, q: 0.7 },
      { type: 'lowpass', freq: 1800 },
    ]);
    k.every(5_000, 12_000, () =>
      k.burst('pink', { level: 0.12, attack: 1.4, decay: 2.2, chain: [{ type: 'bandpass', freq: 500 + k.rnd() * 400, q: 1.2 }] }),
    );
  },
  // Waves: each one swells, breaks and washes out, 6–10 s apart.
  waves(k) {
    k.bed('brown', 0.18, [{ type: 'lowpass', freq: 220 }]);
    const surf = k.bed('pink', 0.05, [
      { type: 'lowpass', freq: 2200 },
      { type: 'highpass', freq: 120 },
    ]);
    k.every(6_000, 10_000, () => {
      const t = k.ctx.currentTime;
      surf.gain.cancelScheduledValues(t);
      surf.gain.setValueAtTime(surf.gain.value, t);
      surf.gain.linearRampToValueAtTime(0.55, t + 2.4);
      surf.gain.setTargetAtTime(0.05, t + 2.6, 1.6);
      k.burst('white', { at: t + 2.3, level: 0.08, attack: 0.4, decay: 2.6, chain: [{ type: 'highpass', freq: 3000 }] });
    });
  },
  // A quiet room, rain against the glass, and a page turning now and then.
  pages_rain(k) {
    k.bed('brown', 0.22, [{ type: 'lowpass', freq: 180 }]);
    const glass = k.bed('pink', 0.22, [
      { type: 'highpass', freq: 1400 },
      { type: 'lowpass', freq: 6500 },
    ]);
    k.lfo(glass.gain, 0.05, 0.06);
    k.every(250, 900, () =>
      k.burst('white', { level: 0.05 + k.rnd() * 0.06, attack: 0.003, decay: 0.09, chain: [{ type: 'bandpass', freq: 4500 + k.rnd() * 2500, q: 10 }] }),
    );
    k.every(9_000, 20_000, () => {
      const t = k.ctx.currentTime;
      k.burst('pink', { at: t, level: 0.14, attack: 0.05, decay: 0.32, chain: [{ type: 'bandpass', freq: 2200, q: 1.4 }] });
      k.burst('pink', { at: t + 0.18, level: 0.08, attack: 0.03, decay: 0.2, chain: [{ type: 'bandpass', freq: 3200, q: 1.8 }] });
    });
  },
  // Two crickets (different pitch and rhythm) over a campfire.
  crickets_fire(k) {
    k.bed('brown', 0.32, [{ type: 'lowpass', freq: 420 }]);
    for (const [freq, base] of [
      [4300, 900],
      [4750, 1300],
    ] as const) {
      const c = k.tone(freq, 0, 'sine');
      k.every(base, base * 2.2, () => {
        const t = k.ctx.currentTime;
        for (let i = 0; i < 3; i++) {
          const at = t + i * 0.06;
          c.gain.gain.setValueAtTime(0, at);
          c.gain.gain.linearRampToValueAtTime(0.035, at + 0.012);
          c.gain.gain.linearRampToValueAtTime(0, at + 0.04);
        }
      });
    }
    k.every(90, 420, () =>
      k.burst('white', { level: 0.08 + k.rnd() * 0.18, attack: 0.001, decay: 0.03 + k.rnd() * 0.05, chain: [{ type: 'highpass', freq: 1500 + k.rnd() * 2000 }] }),
    );
  },
  // A soft fire and a warm, slowly breathing chord.
  fire_pad(k) {
    k.bed('brown', 0.26, [{ type: 'lowpass', freq: 380 }]);
    k.every(200, 700, () =>
      k.burst('white', { level: 0.04 + k.rnd() * 0.08, attack: 0.001, decay: 0.04, chain: [{ type: 'highpass', freq: 1800 }] }),
    );
    const pad = k.gain(0.5);
    const warm = k.filters([{ type: 'lowpass', freq: 900, q: 0.4 }], pad);
    for (const f of [110, 138.59, 164.81, 207.65]) {
      const v = k.tone(f, 0.045, 'triangle', warm);
      v.osc.detune.value = (k.rnd() - 0.5) * 12;
      k.lfo(v.gain.gain, 0.05 + k.rnd() * 0.05, 0.02);
    }
  },
};

export interface AmbienceState {
  recipe: AmbienceRecipe | null;
  volume: number;
  muted: boolean;
  ducked: boolean;
  /** An AudioContext exists (false whenever muted or idle — zero cost). */
  running: boolean;
}

/**
 * Plays one recipe at a time. Call setRecipe / setMuted / setVolume / duck as
 * the UI changes; dispose() when leaving. Never throws on a missing WebAudio.
 */
export class AmbienceEngine {
  private ctx: AudioCtx | null = null;
  private master: GainNode | null = null;
  private kit: Kit | null = null;
  private s: AmbienceState = { recipe: null, volume: DEFAULT_VOLUME, muted: false, ducked: false, running: false };
  private readonly make: () => AudioCtx | null;
  private readonly rnd: () => number;
  private readonly st: (fn: () => void, ms: number) => unknown;
  private readonly ct: (id: unknown) => void;
  private gesture: (() => void) | null = null;
  private swelling = false;
  private swellTimer: unknown = null;

  constructor(deps: AmbienceDeps = {}) {
    this.make = deps.createContext ?? defaultContext;
    this.rnd = deps.random ?? Math.random;
    this.st = deps.setTimeout ?? ((fn, ms) => globalThis.setTimeout(fn, ms));
    this.ct = deps.clearTimeout ?? ((id) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>));
  }

  get state(): AmbienceState {
    return { ...this.s, running: !!this.ctx };
  }

  /** Active timers (tests: zero while muted). */
  get timers(): number {
    return this.kit?.timerCount ?? 0;
  }

  setRecipe(recipe: AmbienceRecipe | null) {
    if (recipe === this.s.recipe) return;
    this.s.recipe = recipe;
    this.teardown();
    this.sync();
  }

  setMuted(muted: boolean) {
    if (muted === this.s.muted) return;
    this.s.muted = muted;
    this.sync();
  }

  setVolume(volume: number) {
    const v = Math.min(1, Math.max(0, Number.isFinite(volume) ? volume : DEFAULT_VOLUME));
    if (v === this.s.volume) return;
    this.s.volume = v;
    if (v === 0) this.sync();
    else if (!this.ctx) this.sync();
    else this.applyLevel(0.25);
  }

  /** Dip while your own mic is active (fast down, gentle back up). */
  duck(on: boolean) {
    if (on === this.s.ducked) return;
    this.s.ducked = on;
    this.applyLevel(on ? 0.08 : 0.45);
  }

  /**
   * v16: a moment in a game (a reveal, a result) — the place swells for a
   * beat (rain picks up, the waves roll in louder) and settles back. A
   * no-op while silent; never louder than MASTER_MAX.
   */
  swell(ms = SWELL_MS) {
    if (!this.ctx || !this.master) return;
    if (this.swellTimer !== null) this.ct(this.swellTimer);
    this.swelling = true;
    this.applyLevel(0.2);
    this.swellTimer = this.st(() => {
      this.swellTimer = null;
      this.swelling = false;
      this.applyLevel(0.8);
    }, ms);
  }

  dispose() {
    this.s.recipe = null;
    this.teardown();
  }

  private target(): number {
    return Math.min(MASTER_MAX, MASTER_MAX * this.s.volume * (this.s.ducked ? DUCK_LEVEL : 1) * (this.swelling ? SWELL_LEVEL : 1));
  }

  private applyLevel(timeConstant: number) {
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(this.target(), t, timeConstant);
  }

  private sync() {
    const want = !!this.s.recipe && !this.s.muted && this.s.volume > 0;
    if (!want) return this.teardown();
    if (this.ctx) return this.applyLevel(0.25);
    const ctx = this.make();
    if (!ctx) return;
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);
    this.master = master;
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(this.target(), ctx.currentTime + FADE_IN_S);
    this.kit = new Kit(ctx, master, this.rnd, this.st, this.ct);
    RECIPES[this.s.recipe!](this.kit);
    if (ctx.state === 'suspended') this.resumeOnGesture();
  }

  /** Autoplay rules: resume on the next tap or key press. */
  private resumeOnGesture() {
    if (typeof window === 'undefined' || this.gesture) return;
    const go = () => {
      void this.ctx?.resume().catch(() => undefined);
      this.dropGesture();
    };
    this.gesture = go;
    window.addEventListener('pointerdown', go, { once: true });
    window.addEventListener('keydown', go, { once: true });
  }

  private dropGesture() {
    if (!this.gesture || typeof window === 'undefined') return;
    window.removeEventListener('pointerdown', this.gesture);
    window.removeEventListener('keydown', this.gesture);
    this.gesture = null;
  }

  /** Fade out, stop every source and timer, close the context. */
  private teardown() {
    this.dropGesture();
    if (this.swellTimer !== null) this.ct(this.swellTimer);
    this.swellTimer = null;
    this.swelling = false;
    const ctx = this.ctx;
    const master = this.master;
    const kit = this.kit;
    this.ctx = this.master = this.kit = null;
    if (!ctx) return;
    const t = ctx.currentTime;
    try {
      master?.gain.cancelScheduledValues(t);
      master?.gain.setTargetAtTime(0, t, FADE_OUT_S / 4);
    } catch {
      /* closed */
    }
    kit?.stop(t + FADE_OUT_S);
    this.st(() => void ctx.close().catch(() => undefined), FADE_OUT_S * 1000 + 50);
  }
}

/** A one-off preview (the lobby teaser): plays a recipe for `ms`, then fades. */
export function previewAmbience(recipe: AmbienceRecipe, opts: AmbienceDeps & { ms?: number; volume?: number } = {}): () => void {
  const engine = new AmbienceEngine(opts);
  engine.setVolume(opts.volume ?? DEFAULT_VOLUME);
  engine.setRecipe(recipe);
  const st = opts.setTimeout ?? ((fn: () => void, ms: number) => globalThis.setTimeout(fn, ms));
  const ct = opts.clearTimeout ?? ((id: unknown) => globalThis.clearTimeout(id as ReturnType<typeof setTimeout>));
  const id = st(() => engine.dispose(), opts.ms ?? 5000);
  return () => {
    ct(id);
    engine.dispose();
  };
}
